import React, { createContext, useContext, useState, useEffect } from 'react';
import { subscribeToSiteConfig, updateSiteConfig, initSiteConfig } from '../services/firestoreService';

const SiteConfigContext = createContext();

// Backend API URL
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}

export function SiteConfigProvider({ children }) {
  // Başlangıç değerlerini boş bırakıyoruz - Flash sorununu önlemek için
  const [config, setConfig] = useState({
    siteTitle: '',
    siteSubtitle: '',
    primaryColor: '#f59e0b',
    logoLight: '',
    logoDark: '',
    loginImage: '',
    favicon: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize config if not exists
    initSiteConfig();

    const unsubscribe = subscribeToSiteConfig((newConfig) => {
      if (newConfig) {
        setConfig(prev => ({ ...prev, ...newConfig }));
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Update favicon dynamically
  useEffect(() => {
    if (config.favicon) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = config.favicon.startsWith('/api') ? `${API_URL}${config.favicon}` : config.favicon;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [config.favicon]);

  // Update document title
  useEffect(() => {
    document.title = `${config.siteTitle} - ${config.siteSubtitle}`;
  }, [config.siteTitle, config.siteSubtitle]);

  // Update CSS variable for primary color
  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', config.primaryColor);
    // Generate lighter and darker variants
    const hex = config.primaryColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    document.documentElement.style.setProperty('--primary-color-light', `rgba(${r}, ${g}, ${b}, 0.1)`);
    document.documentElement.style.setProperty('--primary-color-dark', `rgba(${Math.max(0, r-30)}, ${Math.max(0, g-30)}, ${Math.max(0, b-30)}, 1)`);
  }, [config.primaryColor]);

  // Dosyayı kendi backend'imize yükle (Firebase Storage yerine)
  const uploadFile = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await fetch(`${API_URL}/api/upload?type=${type}`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Dosya yüklenemedi');
    }

    const data = await response.json();
    // Backend'den dönen URL'yi tam URL olarak döndür
    return `${API_URL}${data.url}`;
  };

  const updateConfig = async (updates) => {
    await updateSiteConfig(updates);
  };

  const value = {
    config,
    loading,
    updateConfig,
    uploadFile
  };

  return (
    <SiteConfigContext.Provider value={value}>
      {children}
    </SiteConfigContext.Provider>
  );
}
